import { useState } from "react";
import { makeStyles } from "@material-ui/styles";
import classNames from "classnames";
import BoxControls from "../../Common/Box/BoxControls";
import ScenarioChooser from "../../Common/Box/Choosers/ScenarioChooser";
import Table from "../../Common/Box/Table";
import initialData from "../../../data/initialData.json";
import useAreaQuery from "../../../hooks/useAreaQuery";
import useCsvDownload from "../../../hooks/useCsvDownload";
import {
  formatNumber,
  formatPercentage,
  getDownloadFileStem,
  getScenario,
} from "../../../utilities";

const key = "MH_t3";
const number = 7;

const format = (n) =>
  formatNumber(n, {
    prefix: "$",
    significantDigits: 3,
  });

const getScenarioDescription = (scenarioId) => {
  switch (scenarioId) {
    case 5:
      return "Scenario 1: Maintain Coverage";

    case 1:
      return "Scenario 2: Increase Coverage";

    case 104:
      return "Scenario 3: Increase Coverage and Scale-Up Emerging Drugs";

    default:
      return "Selected Scenario";
  }
};

const useStyles = makeStyles((theme) => ({
  changeGroupBoundary: {
    borderLeft: `1px solid ${theme.table.border.primary}`,
  },
  nameGroupBoundary: {
    borderRight: `1px solid ${theme.table.border.primary}`,
  },
  table: {
    borderCollapse: "collapse",
    fontSize: theme.table.fontSize,
    "& tbody tr": {
      borderTop: `1px solid ${theme.table.border.primary}`,
    },
    "& tbody tr:first-child": {
      borderTop: `1px solid ${theme.table.border.secondary}`,
    },
    "& tbody tr:last-child": {
      fontWeight: "bold",
      borderTop: `1px solid ${theme.table.border.secondary}`,
    },
    "& td": {
      padding: theme.table.cellPadding,
      textAlign: "right",
      whiteSpace: "nowrap",
    },
    "& td.category": {
      textAlign: "left",
    },
    "& th": {
      padding: theme.table.cellPadding,
      whiteSpace: "nowrap",
    },
    "& tr:last-child th:not(.category)": {
      minWidth: "8rem",
    },
  },
}));

const Component = () => {
  const [selectedScenarioId, setSelectedScenarioId] = useState(104);

  const { areaData: data } = useAreaQuery(
    `/mh/tables/3/${selectedScenarioId}`,
    initialData["mh_t3"]
  );

  const downloadCsv = useCsvDownload();

  const classes = useStyles();

  const handleScenarioChange = (scenarioId) => {
    setSelectedScenarioId(scenarioId);
  };

  const handleCsvDownload = () => {
    const fileStem = getDownloadFileStem(
      key,
      number,
      getScenario(selectedScenarioId)
    );

    downloadCsv(`/mh/tables/3/${selectedScenarioId}/csv`, `${fileStem}.csv`);
  };

  return (
    <Table
      key={key}
      number={number.toString()}
      title="Change in Cost of Nine Key Maternal Health Supplies"
      subtitle={`By ${getScenarioDescription(
        selectedScenarioId
      )} and Income Group, 2019-2030`}
      controls={
        <BoxControls>
          <ScenarioChooser
            area="maternal-health"
            value={selectedScenarioId}
            onChange={handleScenarioChange}
          />
        </BoxControls>
      }
      onDownloadCsv={handleCsvDownload}
    >
      <table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.nameGroupBoundary} />
            <th colSpan={3}>Total Costs</th>
            <th colSpan={2} className={classes.changeGroupBoundary}>
              Change (2019 to 2030)
            </th>
          </tr>
          <tr>
            <th className={classes.nameGroupBoundary} />
            <th>2019</th>
            <th>2025</th>
            <th>2030</th>
            <th className={classes.changeGroupBoundary}>#</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          {data.map((incomeGroup) => (
            <tr key={incomeGroup.incomeGroup}>
              <td className={classNames("category", classes.nameGroupBoundary)}>
                {incomeGroup.incomeGroup}
              </td>

              {incomeGroup.years.map((year) => (
                <td key={year.year}>{format(year.value)}</td>
              ))}

              <td className={classes.changeGroupBoundary}>
                {format(incomeGroup.change)}
              </td>
              <td>{formatPercentage(incomeGroup.percentage)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Table>
  );
};

export default Component;
